<template>
  <div class="results7-wrap">

    <div class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <div class="brand-mark"></div>
          <div class="brand-text">SEQ <small>Swiss Eating Quiz · Class report for teachers</small></div>
        </div>
        <div class="topbar-meta">CLASS REPORT · 09.05.2026 · 7B</div>
      </div>
    </div>

    <div class="hero">
      <div class="hero-inner">
        <div class="hero-eyebrow">Class report for teachers</div>
        <h1 class="hero-title">How is your <em>class</em> eating?</h1>
        <p class="hero-sub">
          An anonymised overview of your class's eating habits, with conversation starters for the classroom and concrete teaching activities. No individual assessments, no grades. The goal of this report is not diagnosis but awareness and dialogue.
        </p>
      </div>
    </div>

    <div class="meta-grid">
      <div v-for="cell in metaCells" :key="cell.label" class="meta-cell">
        <div class="meta-label">{{ cell.label }}</div>
        <div class="meta-value">{{ cell.value }}</div>
      </div>
    </div>

    <!-- SECTION 1: PARTICIPATION -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">01 / 06</div>
          <div>
            <h2 class="section-title">Participation and <em>data quality</em>.</h2>
            <p class="section-lede">
              Who took the quiz, how carefully did they answer, and what does that tell us about the reliability of the results?
            </p>
          </div>
        </div>

        <div class="participation-grid">
          <div v-for="t in participationTiles" :key="t.label" class="part-tile">
            <div class="part-label">{{ t.label }}</div>
            <div class="part-value">{{ t.value }}<span v-if="t.unit" class="unit">{{ t.unit }}</span></div>
            <div class="part-detail">{{ t.detail }}</div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Note on anonymity</div>
          <div class="card-title">What you as a teacher cannot see</div>
          <p class="card-paragraph">
            This report shows only class averages and distributions. You cannot identify any individual student or trace any specific answer to a person. This is by design: students should feel free to answer honestly without fear of being judged. If a student wants to see their own results, they can do so through their own SEQ access.
          </p>
        </div>
      </div>
    </section>

    <!-- SECTION 2: CLASS PROFILE -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">02 / 06</div>
          <div>
            <h2 class="section-title">Your class <em>profile</em>.</h2>
            <p class="section-lede">
              How does the class's eating behaviour compare to the Swiss Food Pyramid (Schweizer Lebensmittelpyramide)? Averages across all 20 participating students.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Fig. 1 · Distribution of overall scores</div>
          <div class="card-title">How high are the scores in your class?</div>
          <p class="card-sub">
            Number of students per score range. Higher scores mean closer to the Swiss recommendation. A class with similar scores is "uniform"; one with wide spread is "heterogeneous."
          </p>

          <div class="distribution">
            <div v-for="b in distribution" :key="b.label" class="bar" :class="b.cls" :style="{ height: b.height + '%' }">
              <div class="bar-count">{{ b.count }}</div>
              <div class="bar-label">{{ b.label }}</div>
            </div>
          </div>

          <p class="card-paragraph card-paragraph--note">
            <strong>How to read:</strong> The class is heterogeneous. Most students fall in the middle range (56–85), with few at the extremes. This is typical for a 7th-grade class.
            Class average: <strong>67 / 100</strong>. Median: <strong>69 / 100</strong>.
          </p>
        </div>

        <div class="card">
          <div class="card-eyebrow">Fig. 2 · Averages per food group</div>
          <div class="card-title">Where the class is strong, where there are gaps</div>
          <p class="card-sub">
            Class average for each food group. Green = close to the recommendation, amber = slightly below, red = clearly below or above.
          </p>

          <div v-for="g in groupAverages" :key="g.name" class="group-row">
            <div class="group-name"><span class="group-emoji">{{ g.emoji }}</span>{{ g.name }}</div>
            <div class="group-bar"><div class="group-bar-fill" :class="g.cls" :style="{ width: g.score + '%' }"></div></div>
            <div class="group-score">{{ g.score }} / 100</div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Fig. 3 · Notable response patterns</div>
          <div class="card-title">Where students agreed or disagreed most</div>
          <p class="card-sub">
            Response distribution for selected questions. Useful as conversation starters in class.
          </p>

          <div class="split-legend">
            <span><span class="split-legend-dot" style="background:#C8392E"></span>Never / very rarely</span>
            <span><span class="split-legend-dot" style="background:#E8A33D"></span>1–2× / week</span>
            <span><span class="split-legend-dot" style="background:#85BB2F"></span>3–5× / week</span>
            <span><span class="split-legend-dot" style="background:#2D8659"></span>Daily</span>
          </div>

          <div v-for="q in splitQuestions" :key="q.num" class="split-row">
            <div class="split-question"><span class="split-question-num">{{ q.num }}</span>{{ q.question }}</div>
            <div class="split-bar">
              <div
                v-for="(seg, i) in q.segments"
                :key="i"
                class="split-seg"
                :class="seg.cls"
                :style="{ width: seg.width + '%' }"
              >{{ seg.count || '' }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 3: KEY INSIGHTS -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">03 / 06</div>
          <div>
            <h2 class="section-title">What stands <em>out</em>.</h2>
            <p class="section-lede">
              The three or four patterns most relevant for your teaching. Strengths, gaps, and surprises.
            </p>
          </div>
        </div>

        <div class="insight-grid">
          <div v-for="ins in insights" :key="ins.title" class="insight-card" :class="ins.level">
            <div class="insight-eyebrow">{{ ins.eyebrow }}</div>
            <div class="insight-title">{{ ins.title }}</div>
            <div class="insight-body" v-html="ins.body"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 4: DISCUSSION PROMPTS -->
    <section class="section teal">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">04 / 06</div>
          <div>
            <h2 class="section-title">Conversation starters for the <em>classroom</em>.</h2>
            <p class="section-lede">
              Concrete questions you can ask your class. Each is anchored to a specific result, without exposing any individual. Suitable for 15- to 30-minute classroom discussions.
            </p>
          </div>
        </div>

        <div v-for="d in discussions" :key="d.title" class="discussion-card">
          <div class="discussion-eyebrow">Anchor: {{ d.anchor }}</div>
          <div class="discussion-title">{{ d.title }}</div>
          <div class="discussion-prompt">{{ d.prompt }}</div>
          <div class="discussion-followup">
            <strong>Follow-up questions:</strong> {{ d.followup }}
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 5: ACTIVITIES -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">05 / 06</div>
          <div>
            <h2 class="section-title">Follow-up <em>activities</em>.</h2>
            <p class="section-lede">
              Concrete teaching units that connect to the results. Small and large, indoors and outdoors, single lesson or project week.
            </p>
          </div>
        </div>

        <div class="activity-grid">
          <div v-for="a in activities" :key="a.title" class="activity-card">
            <span class="activity-tag">{{ a.tag }}</span>
            <div class="activity-title">{{ a.title }}</div>
            <div class="activity-body">{{ a.body }}</div>
            <div class="activity-meta">
              <span>{{ a.duration }}</span>
              <span>{{ a.subject }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 6: BENCHMARK + NOTES -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">06 / 06</div>
          <div>
            <h2 class="section-title">Comparison and <em>notes</em>.</h2>
            <p class="section-lede">
              How does your class compare to other 7th-grade classes that have taken the SEQ? Plus space for your own observations.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Table 1 · Class comparison</div>
          <div class="card-title">Your class vs. Swiss 7th-graders</div>
          <p class="card-sub">
            Values from the anonymised SEQ pool. As of 9 May 2026, n = 1,247 students across 65 classes. The comparison is for orientation, not assessment.
          </p>

          <div class="benchmark-row head">
            <div>Food group</div>
            <div class="benchmark-val">Your class</div>
            <div class="benchmark-val">CH average</div>
            <div class="benchmark-trend">Position</div>
          </div>
          <div v-for="row in benchmark" :key="row.name" class="benchmark-row">
            <div class="benchmark-name"><strong v-if="row.bold">{{ row.name }}</strong><template v-else>{{ row.name }}</template></div>
            <div class="benchmark-val"><strong v-if="row.bold">{{ row.you }}</strong><template v-else>{{ row.you }}</template></div>
            <div class="benchmark-val"><strong v-if="row.bold">{{ row.avg }}</strong><template v-else>{{ row.avg }}</template></div>
            <div class="benchmark-trend" :class="row.trendCls">{{ row.trend }}</div>
          </div>

          <p class="card-paragraph card-paragraph--note">
            <strong>In short:</strong> Class 7B is slightly above the Swiss average overall, especially in drinks and vegetables. The class is below the Swiss average in animal proteins, which is not unusual for a 7th-grade class with some emerging vegetarian leanings. The legume gap, however, is a nationwide pattern, not a class-specific problem.
          </p>
        </div>

        <div class="card">
          <div class="card-eyebrow">Your notes</div>
          <div class="card-title">Observations, plans, open questions</div>
          <textarea class="notes-area" placeholder="What did you take away from the results? Which activities are you planning? Where could you use support from parents, school leadership, or external experts?"></textarea>

          <div class="sig-row">
            <div class="sig-line">Teacher, date</div>
            <div class="sig-line">School leadership (optional)</div>
          </div>
        </div>

        <p class="placeholder-note">Demo report. Final content to be developed with BFH Department of Health and primary-/lower-secondary-school teachers.</p>
      </div>
    </section>

    <div class="footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <div class="brand">
            <div class="brand-mark"></div>
            <div class="brand-text">SEQ <small>Swiss Eating Quiz · Class report for teachers</small></div>
          </div>
          <p>
            The SEQ is a research instrument developed at Bern University of Applied Sciences for assessing eating habits. This class report is intended solely for teaching purposes and not for individual evaluation of students.
          </p>
        </div>
        <div class="footer-meta">
          Demo report · Sample data<br>
          v0.1 · May 2026<br>
          BFH Department of Health
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Results7',
  data() {
    return {
      metaCells: [
        { label: 'Class', value: '7B' },
        { label: 'Number of students', value: '22 (age 13–14)' },
        { label: 'Quiz date', value: '9 May 2026, during class' },
        { label: 'Teacher', value: '[ to be filled ]' },
      ],
      participationTiles: [
        { label: 'Took the quiz', value: '20', unit: '/ 22', detail: '2 students absent or did not complete. Strong participation rate (91 %).' },
        { label: 'Completed in full', value: '19', unit: '/ 20', detail: 'One student stopped about halfway. Overall data quality is high.' },
        { label: 'Average time', value: '11', unit: 'min.', detail: 'Expected range is 8–15 min. No one clicked through unrealistically fast.' },
        { label: 'Language', value: 'DE', unit: '', detail: 'All students used the German version of the quiz.' },
      ],
      distribution: [
        { label: '0–40', count: 1, height: 25, cls: 'warn' },
        { label: '41–55', count: 3, height: 50, cls: 'warn' },
        { label: '56–70', count: 7, height: 100, cls: '' },
        { label: '71–85', count: 6, height: 90, cls: 'good' },
        { label: '86–100', count: 3, height: 45, cls: 'good' },
      ],
      groupAverages: [
        { emoji: '💧', name: 'Drinks', score: 78, cls: 'good' },
        { emoji: '🥬', name: 'Vegetables', score: 72, cls: 'good' },
        { emoji: '🥛', name: 'Dairy', score: 70, cls: 'good' },
        { emoji: '🍎', name: 'Fruit', score: 65, cls: 'warn' },
        { emoji: '🥩', name: 'Animal proteins', score: 62, cls: 'warn' },
        { emoji: '🌾', name: 'Grains', score: 58, cls: 'warn' },
        { emoji: '🌱', name: 'Plant proteins', score: 38, cls: 'bad' },
      ],
      splitQuestions: [
        {
          num: 'Q15', question: 'How often do you eat fresh salads?',
          segments: [
            { cls: 'never', width: 15, count: 3 },
            { cls: 'rare', width: 35, count: 7 },
            { cls: 'weekly', width: 35, count: 7 },
            { cls: 'daily', width: 15, count: 3 },
          ],
        },
        {
          num: 'Q23', question: 'How often do you eat fresh berries?',
          segments: [
            { cls: 'never', width: 45, count: 9 },
            { cls: 'rare', width: 35, count: 7 },
            { cls: 'weekly', width: 15, count: 3 },
            { cls: 'daily', width: 5, count: 1 },
          ],
        },
        {
          num: 'Q39', question: 'How often do you eat legumes (lentils, chickpeas)?',
          segments: [
            { cls: 'never', width: 65, count: 13 },
            { cls: 'rare', width: 25, count: 5 },
            { cls: 'weekly', width: 10, count: 2 },
            { cls: 'daily', width: 0, count: 0 },
          ],
        },
        {
          num: 'Q47', question: 'How often do you eat whole-grain bread?',
          segments: [
            { cls: 'never', width: 30, count: 6 },
            { cls: 'rare', width: 30, count: 6 },
            { cls: 'weekly', width: 25, count: 5 },
            { cls: 'daily', width: 15, count: 3 },
          ],
        },
        {
          num: 'Q65', question: 'How much water do you drink per day?',
          segments: [
            { cls: 'never', width: 10, count: 2 },
            { cls: 'rare', width: 20, count: 4 },
            { cls: 'weekly', width: 40, count: 8 },
            { cls: 'daily', width: 30, count: 6 },
          ],
        },
        {
          num: 'Q67', question: 'How often do you drink sweetened drinks (cola, iced tea, syrup)?',
          segments: [
            { cls: 'daily', width: 10, count: 2 },
            { cls: 'weekly', width: 30, count: 6 },
            { cls: 'rare', width: 35, count: 7 },
            { cls: 'never', width: 25, count: 5 },
          ],
        },
      ],
      insights: [
        { level: '', eyebrow: 'Strength', title: 'Water consumption is solid.',
          body: '<span class="insight-stat">14 of 20</span> students drink water daily or near-daily. <span class="insight-stat">5</span> students drink no sweetened drinks at all. For this age group this is a pleasing result and a strong anchor for further discussion about the importance of hydration.' },
        { level: '', eyebrow: 'Strength', title: 'Vegetables better than expected.',
          body: 'The vegetable score sits at <span class="insight-stat">72 / 100</span>, which is surprisingly good for a 7th-grade class. The school lunch programme may be contributing. The variety differs: some students eat lots of salads, others almost never.' },
        { level: 'attention', eyebrow: 'Big gap', title: 'Legumes are almost entirely absent.',
          body: '<span class="insight-stat">13 of 20</span> students answered "never" or "very rarely" for lentils, chickpeas, beans, or tofu. No one eats them regularly. This is the most pronounced gap in the class and at the same time the biggest opportunity: even one meal per week would raise the class score noticeably.' },
        { level: 'warn', eyebrow: 'Attention', title: 'Whole grains are the exception, not the rule.',
          body: 'Only <span class="insight-stat">8 of 20</span> students regularly eat whole-grain bread, whole-grain pasta, or brown rice. The rest are dominated by white bread, white pasta, and white rice. Not dramatic, but a simple entry point for a teaching unit on "Eating colourful also means eating brown."' },
        { level: 'warn', eyebrow: 'Surprise', title: 'Berries are rarely eaten.',
          body: '<span class="insight-stat">16 of 20</span> students never or rarely eat fresh berries. Seasonality plays a role, as do price and availability. A nice angle for a discussion about seasonal fruit and Swiss berry production.' },
        { level: 'warn', eyebrow: 'Split picture', title: 'Sweetened drinks: two camps.',
          body: '<span class="insight-stat">8 of 20</span> students regularly drink sweetened beverages (cola, iced tea, syrup); the other <span class="insight-stat">12</span> rarely or never. The class is split in two. A classroom conversation could be particularly interesting because of this diversity, without anyone needing to "out" themselves.' },
      ],
      discussions: [
        { anchor: 'Legumes', title: 'What do you actually know about legumes?',
          prompt: 'In our class, 13 out of 20 of us never or almost never eat lentils, beans, or chickpeas. What are legumes? Which ones do you know? Have you ever eaten them, maybe without realising it?',
          followup: 'Hummus? Lentil soup? Falafel? Legumes in chili? What stops you from eating them more often: taste, appearance, or just habit?' },
        { anchor: 'Seasonality', title: 'When is berry season in Switzerland?',
          prompt: 'Surprisingly many in our class never eat berries. Do you know when strawberries, raspberries, or blueberries are in season in Switzerland? What does that mean for the price and for the environment?',
          followup: 'Where can you buy seasonal berries in your area? What does 250 g of strawberries cost in June vs. in December? Why is that?' },
        { anchor: 'Whole grains', title: 'What does "whole grain" actually mean?',
          prompt: "In our class, 12 out of 20 of us rarely eat whole-grain bread. Do you know what \"whole grain\" really means? What's the difference compared to white flour? Why does it make a difference for feeling full?",
          followup: "If you don't like whole grains, what might that be: the texture, the taste, just unfamiliarity? Do you know about whole-grain pasta, brown rice, whole-grain croissants?" },
        { anchor: 'Water vs. sweetened drinks', title: 'Why do we drink what we drink?',
          prompt: "The class is split here. Some of you drink mostly water, others often drink sweetened beverages. Both groups are about the same size. I'm not going to ask who's in which group, but: what are your reasons for your choice?",
          followup: "Taste? Habit? What's available at home? What your friends drink? What would be the easiest way to swap one sweet drink for water each day?" },
        { anchor: 'Advertising and food', title: 'Who actually decides what we eat?',
          prompt: 'Look at the results: the class is really strong in some groups and weaker in others. Do you think that has to do with you personally, or more with what gets bought at home? Or with advertising? Who decides?',
          followup: 'Who does the shopping at home? Who cooks? How much influence do you yourselves have on what\'s in the fridge? What would you change if you were deciding alone?' },
      ],
      activities: [
        { tag: 'One lesson', title: 'Legume tasting', duration: '45 min.', subject: 'Home economics',
          body: 'Hummus, lentil soup, chickpea salad, edamame. Three or four varieties, anonymous voting with sticky dots. Who tries what, who likes what, what was a surprise?' },
        { tag: 'One lesson', title: 'Reading food labels', duration: '45 min.', subject: 'Science / home ec.',
          body: 'Students bring in bread packaging, beverage bottles, and cereal boxes. How do you spot whole grain? How much sugar is in 5 dl of iced tea? Practical label literacy.' },
        { tag: 'One week', title: 'Berry-of-the-week challenge', duration: '1 week', subject: 'Class culture',
          body: 'Each student photographs or describes once per day when they ate berries or other seasonal fruit. End of the week: review with the class. No reward, no ranking.' },
        { tag: 'Project day', title: 'Build the Swiss Food Pyramid', duration: 'Half day', subject: 'Science / art',
          body: 'The class assembles the SGE pyramid together, using real foods or images. What goes where, what surprises, what animals and plants live in Switzerland? Photograph the result for the classroom wall.' },
        { tag: 'Half lesson', title: 'Analyse food advertising', duration: '30 min.', subject: 'Media literacy',
          body: 'Class watches three TV commercials or TikTok clips for food products. What is being sold and how? Who is the target audience? Where are the tricks? Trains critical viewing.' },
        { tag: 'Homework', title: "My week's breakfast", duration: '1 week', subject: 'Independent work',
          body: "Students record or photograph their breakfast for a week. Anonymous collection in class, then look together: what shows up often, what's a nice idea worth borrowing?" },
        { tag: 'Project week', title: 'Class cookbook', duration: '1 week', subject: 'Home ec. / language',
          body: 'Each student brings in a family recipe. The class assembles them into a printed cookbook, organised by pyramid tier. Involve parents and relatives; surface cultural diversity.' },
        { tag: 'One lesson', title: 'Re-quiz in 3 months', duration: '15 min.', subject: 'Class meeting',
          body: "The class takes the SEQ again in three months. Compare with today's results: has anything changed? What do you think? Awareness alone often shifts more than big interventions." },
        { tag: 'Teacher', title: 'SGE teaching materials', duration: 'Research', subject: 'SGE',
          body: 'The Swiss Society for Nutrition (SGE) provides ready-made teaching units, sorted by age group and pyramid tier. Free at sge-ssn.ch / ernaehrungsbildung.' },
      ],
      benchmark: [
        { name: 'Drinks', you: '78', avg: '71', trend: '↑ above', trendCls: 'good' },
        { name: 'Vegetables', you: '72', avg: '63', trend: '↑ above', trendCls: 'good' },
        { name: 'Fruit', you: '65', avg: '62', trend: '≈ similar', trendCls: 'neutral' },
        { name: 'Dairy', you: '70', avg: '73', trend: '≈ similar', trendCls: 'neutral' },
        { name: 'Animal proteins', you: '62', avg: '68', trend: '↓ below', trendCls: 'warn' },
        { name: 'Grains', you: '58', avg: '60', trend: '≈ similar', trendCls: 'neutral' },
        { name: 'Plant proteins', you: '38', avg: '42', trend: '≈ similar', trendCls: 'neutral' },
        { name: 'Overall score', you: '67', avg: '64', trend: '↑ slightly above', trendCls: 'good', bold: true },
      ],
    };
  },
};
</script>

<style scoped>
.results7-wrap {
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-dim: #9C9C9C;
  --rule: #DDDDDD;
  --paper: #FAFAF7;
  --card: #FFFFFF;
  --warm: #F2EDE4;
  --good: #2D8659;
  --good-bg: #B8D8C5;
  --warn: #E8A33D;
  --warn-bg: #F5D9A8;
  --bad: #C8392E;
  --bad-bg: #ECB8B3;
  --neutral: #4A6FA5;
  --neutral-bg: #C8D4E6;
  --accent-rule: #DA291C;
  --teal: #2E8A8A;
  --teal-bg: #C5DEDE;
  --mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink);
  background: var(--paper);
  min-height: 100%;
}
.results7-wrap *,
.results7-wrap *::before,
.results7-wrap *::after {
  box-sizing: border-box;
}

/* TOPBAR */
.topbar {
  background: var(--ink);
  color: white;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  padding: 12px 32px;
}
.topbar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;
}
.brand { display: flex; align-items: center; gap: 12px; }
.brand-mark { width: 18px; height: 18px; background: var(--accent-rule); }
.brand-text {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
}
.brand-text small {
  display: block;
  font-weight: 400;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--ink-dim);
  margin-top: -2px;
}
.topbar-meta { font-size: 11px; }

/* HERO */
.hero {
  background: var(--card);
  padding: 32px;
  border-bottom: 1px solid var(--rule);
}
.hero-inner { max-width: 1100px; margin: 0 auto; }
.hero-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--teal);
  margin-bottom: 12px;
}
.hero-title {
  font-size: 36px;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 12px 0;
  letter-spacing: -0.01em;
}
.hero-title em { font-style: italic; color: var(--teal); }
.hero-sub {
  color: var(--ink-mute);
  font-size: 15px;
  max-width: 680px;
  margin: 0;
}

/* META */
.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  background: var(--warm);
}
.meta-cell {
  padding: 14px 20px;
  border-right: 1px solid var(--rule);
  font-size: 12px;
}
.meta-cell:last-child { border-right: none; }
.meta-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.meta-value { font-weight: 500; color: var(--ink); }

/* SECTION */
.section {
  padding: 36px 32px;
  border-bottom: 1px solid var(--rule);
}
.section.warm { background: var(--warm); }
.section.teal { background: var(--teal-bg); }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-header {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 24px;
  margin-bottom: 24px;
}
.section-num {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  color: var(--ink-dim);
  padding-top: 8px;
  border-top: 1px solid var(--ink);
}
.section-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.2;
}
.section-title em { font-style: italic; color: var(--teal); }
.section-lede {
  color: var(--ink-mute);
  font-size: 14px;
  max-width: 780px;
}

/* CARD */
.card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 22px;
  margin-bottom: 14px;
}
.card-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-dim);
  margin-bottom: 4px;
}
.card-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}
.card-sub {
  color: var(--ink-mute);
  font-size: 13px;
  margin-bottom: 18px;
}
.card-paragraph {
  color: var(--ink-mute);
  margin: 0;
}
.card-paragraph--note {
  margin-top: 32px;
  font-size: 13px;
}

/* PARTICIPATION GRID */
.participation-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 14px;
}
.part-tile {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 16px 18px;
}
.part-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 6px;
}
.part-value {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.1;
  color: var(--ink);
}
.part-value .unit {
  font-size: 13px;
  font-weight: 400;
  color: var(--ink-mute);
  margin-left: 4px;
}
.part-detail {
  font-size: 12px;
  color: var(--ink-mute);
  margin-top: 6px;
  line-height: 1.5;
}

/* DISTRIBUTION CHART */
.distribution {
  display: flex;
  gap: 4px;
  align-items: flex-end;
  height: 180px;
  padding: 10px 0 24px 0;
  border-bottom: 1px solid var(--ink);
  position: relative;
}
.bar {
  flex: 1;
  background: var(--teal);
  position: relative;
  border-radius: 2px 2px 0 0;
  min-height: 4px;
}
.bar.good { background: var(--good); }
.bar.warn { background: var(--warn); }
.bar-label {
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-mute);
}
.bar-count {
  position: absolute;
  top: -18px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: var(--mono);
  font-size: 11px;
  font-weight: 600;
  color: var(--ink);
}

/* GROUP BARS */
.group-row {
  display: grid;
  grid-template-columns: 160px 1fr 80px;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--rule);
  align-items: center;
}
.group-row:last-child { border-bottom: none; }
.group-name {
  font-weight: 500;
  font-size: 13px;
}
.group-emoji { margin-right: 6px; }
.group-bar {
  background: var(--warm);
  height: 12px;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}
.group-bar-fill {
  height: 100%;
  border-radius: 2px;
}
.group-bar-fill.good { background: var(--good); }
.group-bar-fill.warn { background: var(--warn); }
.group-bar-fill.bad { background: var(--bad); }
.group-score {
  font-family: var(--mono);
  font-size: 13px;
  font-weight: 600;
  text-align: right;
}

/* INSIGHTS */
.insight-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.insight-card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 18px 20px;
  border-left: 4px solid var(--good);
}
.insight-card.warn { border-left-color: var(--warn); }
.insight-card.attention { border-left-color: var(--bad); }
.insight-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--good);
  margin-bottom: 8px;
}
.insight-card.warn .insight-eyebrow { color: var(--warn); }
.insight-card.attention .insight-eyebrow { color: var(--bad); }
.insight-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}
.insight-body {
  font-size: 13px;
  color: var(--ink-soft);
  line-height: 1.6;
}
.insight-body :deep(.insight-stat) {
  font-family: var(--mono);
  font-weight: 600;
  background: var(--warm);
  padding: 1px 6px;
  border-radius: 2px;
}

/* DISCUSSION CARDS */
.discussion-card {
  background: var(--teal-bg);
  border: 1px solid var(--teal);
  padding: 18px 22px;
  margin-bottom: 12px;
}
.discussion-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--teal);
  margin-bottom: 6px;
}
.discussion-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}
.discussion-prompt {
  font-size: 14px;
  font-style: italic;
  color: var(--ink-soft);
  line-height: 1.6;
  padding: 8px 14px;
  border-left: 3px solid var(--teal);
  background: rgba(255,255,255,0.5);
  margin-bottom: 8px;
}
.discussion-followup {
  font-size: 12px;
  color: var(--ink-mute);
  line-height: 1.5;
}

/* ACTIVITY CARDS */
.activity-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}
.activity-card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 18px 18px 14px 18px;
  display: flex;
  flex-direction: column;
}
.activity-tag {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--teal);
  background: var(--teal-bg);
  padding: 2px 8px;
  border-radius: 2px;
  display: inline-block;
  margin-bottom: 10px;
  align-self: flex-start;
}
.activity-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
}
.activity-body {
  font-size: 13px;
  color: var(--ink-mute);
  line-height: 1.6;
  flex: 1;
  margin-bottom: 12px;
}
.activity-meta {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-mute);
  border-top: 1px solid var(--rule);
  padding-top: 8px;
  display: flex;
  justify-content: space-between;
}

/* BENCHMARK */
.benchmark-row {
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid var(--rule);
  align-items: center;
  font-size: 13px;
}
.benchmark-row:last-child { border-bottom: none; }
.benchmark-row.head {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  border-bottom: 1px solid var(--ink);
}
.benchmark-name { font-weight: 500; }
.benchmark-val {
  font-family: var(--mono);
  font-weight: 600;
  text-align: right;
  min-width: 60px;
}
.benchmark-trend {
  font-family: var(--mono);
  font-size: 11px;
  text-align: right;
  min-width: 80px;
}
.benchmark-trend.good { color: var(--good); }
.benchmark-trend.warn { color: var(--warn); }
.benchmark-trend.bad { color: var(--bad); }
.benchmark-trend.neutral { color: var(--ink-mute); }

/* QUESTIONS WITH SPLIT */
.split-row {
  display: grid;
  grid-template-columns: 1fr 240px;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid var(--rule);
  align-items: center;
}
.split-row:last-child { border-bottom: none; }
.split-question { font-size: 13px; }
.split-question-num {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-dim);
  margin-right: 6px;
}
.split-bar {
  display: flex;
  height: 18px;
  border-radius: 2px;
  overflow: hidden;
  border: 1px solid var(--rule);
}
.split-seg {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 600;
  color: white;
}
.split-seg.never { background: #C8392E; }
.split-seg.rare { background: #E8A33D; }
.split-seg.weekly { background: #85BB2F; }
.split-seg.daily { background: #2D8659; }

.split-legend {
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-mute);
}
.split-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 4px;
  vertical-align: middle;
  border-radius: 1px;
}

/* NOTES */
.notes-area {
  width: 100%;
  border: 1px solid var(--rule);
  padding: 12px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  min-height: 120px;
  background: var(--card);
  resize: vertical;
  outline: none;
}
.notes-area:focus { outline: 1px solid var(--teal); outline-offset: -1px; }

.sig-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 24px;
}
.sig-line {
  border-top: 1px solid var(--ink);
  padding-top: 6px;
  font-size: 11px;
  color: var(--ink-mute);
  font-family: var(--mono);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.placeholder-note {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-dim);
  text-align: center;
  margin-top: 14px;
}

/* FOOTER */
.footer {
  background: var(--ink);
  color: white;
  padding: 32px;
  font-size: 12px;
}
.footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
}
.footer p {
  margin: 8px 0 0 0;
  color: var(--ink-dim);
  max-width: 600px;
}
.footer-meta {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-dim);
  text-align: right;
}

/* PRINT */
@media print {
  .results7-wrap { background: white; }
  .topbar { background: white; color: black; }
  .hero, .card, .part-tile, .insight-card, .activity-card, .discussion-card { background: white; }
  .section { padding: 20px; border: none; }
  .no-print { display: none; }
}
</style>
